import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import WorkflowPanel from './WorkflowPanel';
import './Terminal.css';

const Terminal = ({ sessionId, onComplete, onClose, onError, onProgress }) => {
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [workflowGraph, setWorkflowGraph] = useState(null);
  const [agents, setAgents] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const terminalRef = useRef(null);
  const logsEndRef = useRef(null);
  const socketRef = useRef(null);
  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const lastLogRef = useRef({ type: '', message: '' });

  // Fetch the workflow shape so the DAG is visible before execution begins.
  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/config')
      .then((res) => {
        if (!cancelled && res.data?.workflow) setWorkflowGraph(res.data.workflow);
      })
      .catch(() => {
        /* the panel simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    // Clean up any existing socket first
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Add initial log message when verification starts
    addLog('info', 'Verification process started...');

    // Same-origin by default so the app works on localhost, the dev container
    // and production without code changes. REACT_APP_SOCKET_URL can override.
    const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    // Start on long-polling and let Socket.IO upgrade to WebSocket only if the
    // handshake succeeds. Some deployments sit behind proxies that reject the
    // upgrade, and a websocket-first config leaves the client with no transport
    // at all instead of falling back.
    const newSocket = io(socketUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnectionAttempts: 10,
      timeout: 20000,
    });
    newSocket.on('connect', () => {
      // Join per-session room to avoid cross-session broadcasts
      newSocket.emit('join_session', { session_id: sessionId });
      addLog('system', 'Connected to VeryNews verification server');
    });

    newSocket.off('progress_update');
    newSocket.on('progress_update', (data) => {
      if (data.session_id === sessionId) {
        setProgress(data.progress);
        setCurrentStep(data.message);
        if (onProgressRef.current) {
          onProgressRef.current(data.progress, data.message);
        }
      }
    });

    newSocket.off('log_message');
    newSocket.on('log_message', (data) => {
      if (data.session_id === sessionId) {
        addLog(data.type, data.message);
      }
    });

    newSocket.off('agent_update');
    newSocket.on('agent_update', (data) => {
      if (data.session_id !== sessionId) return;
      if (data.node?.id) {
        setAgents((prev) => ({ ...prev, [data.node.id]: data.node }));
      }
      if (typeof data.progress === 'number') {
        setProgress(data.progress);
        if (onProgressRef.current) {
          onProgressRef.current(data.progress, data.node?.detail || '');
        }
      }
      if (typeof data.elapsed_total === 'number') setElapsed(data.elapsed_total);
    });

    newSocket.off('verification_complete');
    newSocket.on('verification_complete', (data) => {
      if (data.session_id === sessionId) {
        setIsComplete(true);
        addLog('success', '🎉 Verification completed! Redirecting to report...');
        setTimeout(() => {
          if (onCompleteRef.current) {
            onCompleteRef.current(data);
          }
        }, 1000);
      }
    });

    newSocket.off('verification_error');
    newSocket.on('verification_error', (data) => {
      if (data.session_id === sessionId) {
        addLog('error', `❌ Verification failed: ${data.error}`);
        if (onErrorRef.current) {
          onErrorRef.current(data.error);
        }
      }
    });

    socketRef.current = newSocket;

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Auto scroll to bottom
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const addLog = (type, message) => {
    const timestamp = new Date().toLocaleTimeString();
    // Deduplicate consecutive identical logs
    const last = lastLogRef.current;
    if (last.type === type && last.message === message) {
      return;
    }
    lastLogRef.current = { type, message };
    setLogs(prev => [...prev, {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp
    }]);
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'system': return '🔌';
      case 'info': return '📋';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'progress': return '⚡';
      default: return '📝';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'system': return '#4ecdc4';
      case 'info': return '#45b7d1';
      case 'success': return '#96ceb4';
      case 'error': return '#ff6b6b';
      case 'progress': return '#ffeaa7';
      default: return '#ffffff';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="terminal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="terminal-window"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.4, type: "spring" }}
        >
          {/* Terminal Header */}
          <div className="terminal-header">
            <div className="terminal-controls">
              <div className="control-button close" onClick={onClose}></div>
              <div className="control-button minimize"></div>
              <div className="control-button maximize"></div>
            </div>
            <div className="terminal-title">
    
              <span>VeryNews Verification Terminal</span>
            </div>
            <button className="close-button" onClick={isComplete ? onClose : undefined} disabled={!isComplete}>
              {/* <X size={18} /> */}
            </button>
          </div>

          {/* Progress Bar */}
          <div className="terminal-progress">
            <div className="progress-bar">
              <motion.div
                className="progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            <div className="progress-text">
              {progress}% - {currentStep}
            </div>
          </div>

          {/* Terminal Content */}
          <div className="terminal-content" ref={terminalRef}>
            {/* Live agent workflow: the DAG is drawn before execution starts,
                then each node lights up as agent_update events arrive. */}
            <WorkflowPanel graph={workflowGraph} agents={agents} elapsed={elapsed} />

            <div className="terminal-prompt">
              <span className="prompt-symbol">$</span>
              <span className="prompt-text">verynews --verify --multi-agent</span>
            </div>
            
            <div className="terminal-logs">
              {logs.map((log, index) => (
                <motion.div
                  key={log.id}
                  className={`log-line ${log.type}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  style={{ color: getLogColor(log.type) }}
                >
                  <span className="log-timestamp">[{log.timestamp}]</span>
                  <span className="log-icon">{getLogIcon(log.type)}</span>
                  <span className="log-message">{log.message}</span>
                </motion.div>
              ))}
              
              {/* Blinking cursor */}
              {!isComplete && (
                <motion.div
                  className="terminal-cursor"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  █
                </motion.div>
              )}
              
              <div ref={logsEndRef} />
            </div>
          </div>

          {/* Terminal Footer */}
          <div className="terminal-footer">
            <div className="terminal-status">
              {isComplete ? (
                <span className="status-complete">✅ Verification Complete</span>
              ) : (
                <span className="status-running">⚡ Running...</span>
              )}
            </div>
            <div className="terminal-info">
              Session: {sessionId.substring(0, 8)}...
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Terminal; 